import React from 'react';
import { useQuery } from '@apollo/client';
import { TermsAndConditions } from '@shopify-multiseller/graphql/partner/types';
import { Page } from '../../components';
import { AdminUI } from '../../ui/AdminUI';
import { Card } from '../../components/primitives';
import { useAuth } from '../../hooks';
import { formatDate } from '../../helpers/formatters';
import { TermsDocument } from '../../authentication/AcceptTermsAndConditions/TermsDocument';
import { GET_TERMS_AND_CONDITIONS_QUERY } from '../../authentication/AcceptTermsAndConditions/queries';
import { SettingsRail } from './SettingsRail';
import { settingsDestinations } from './destinations';

/**
 * The agreement, readable again after it has been accepted.
 *
 * "Terms & Conditions" in the account menu has always led to a 404: the
 * agreement was only ever shown once, on the way in, and there was no way back
 * to it. Same document column as the acceptance screen, without the acceptance
 * controls.
 */
export const TermsPage: React.FC = () => {
  const auth = useAuth();
  const destinations = settingsDestinations(auth?.user?.seller?.settings?.storefront?.enabled);
  const acceptedAt = auth?.user?.seller?.terms?.acceptedAt;

  const { data, loading } = useQuery<{ termsAndConditions: TermsAndConditions }>(
    GET_TERMS_AND_CONDITIONS_QUERY,
  );
  const terms = data?.termsAndConditions;

  return (
    <AdminUI>
      <Page title="Terms &amp; conditions">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[12rem_1fr]">
          <SettingsRail destinations={destinations} className="lg:sticky lg:top-20 lg:self-start" />
          <div className="min-w-0 max-w-3xl">
            <p className="mb-3 text-sm text-muted-foreground">
              {[
                terms?.version && `Version ${terms.version}`,
                acceptedAt && `accepted ${formatDate(acceptedAt, { year: 'numeric', month: 'long', day: 'numeric' })}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <Card className="overflow-hidden">
              <TermsDocument
                content={terms?.content}
                loading={loading}
                className="h-[32rem] overflow-auto"
              />
            </Card>
          </div>
        </div>
      </Page>
    </AdminUI>
  );
};
